import { startOfDay, subMonths } from 'date-fns'

import type { Income } from '@/types/Activity'
import { parseDate } from '@/utils/date'

export type OpenBankingEligibilityInput = Pick<Income, 'type'> &
  Partial<Pick<Income, 'status' | 'startDate'>>

export const isOpenBankingEligibleIncome = (
  income: OpenBankingEligibilityInput,
): boolean =>
  isEligibleEmploymentIncome(income) || isUnconditionallyEligibleIncomeType(income.type)

const UNCONDITIONALLY_ELIGIBLE_INCOME_TYPES = new Set(['savings', 'pension', 'benefits'])

const isUnconditionallyEligibleIncomeType = (type: string): boolean =>
  UNCONDITIONALLY_ELIGIBLE_INCOME_TYPES.has(normaliseString(type))

const isEligibleEmploymentIncome = (income: OpenBankingEligibilityInput): boolean =>
  isEmploymentIncomeType(income.type) &&
  isNotSelfEmployed(income.status) &&
  meetsEmploymentDurationCriteria(income.startDate)

const isEmploymentIncomeType = (type: string): boolean =>
  normaliseString(type) === 'employment'

const isNotSelfEmployed = (status?: string): boolean =>
  normaliseString(status) !== 'self-employed'

const meetsEmploymentDurationCriteria = (startDate?: string): boolean =>
  employmentHasStarted(startDate) && employedMoreThanTwoMonths(startDate)

const employmentHasStarted = (startDate?: string): boolean =>
  !!startDate &&
  startOfDay(parseDate(startDate)).getTime() <= startOfDay(new Date()).getTime()

const employedMoreThanTwoMonths = (startDate?: string): boolean =>
  !!startDate &&
  startOfDay(parseDate(startDate)).getTime() <
    subMonths(startOfDay(new Date()), 2).getTime()

const normaliseString = (value?: string): string => (value ?? '').trim().toLowerCase()
